import { useState, useEffect, useCallback } from 'react';
import {
  subscribeToCustomers,
  type Customer,
  type CustomerServiceError,
} from '../services/customer.service';

export interface UseCustomersReturn {
  customers: Customer[];
  loading: boolean;
  error: CustomerServiceError | null;
  refetch: () => void;
}

/**
 * Subscribes to the org's customer list in real time.
 * Customers are sorted A-Z by first name (last name as tiebreaker).
 * Pass `null` for orgId when the org context is not yet resolved.
 */
export function useCustomers(orgId: string | null): UseCustomersReturn {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<CustomerServiceError | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick(t => t + 1), []);

  useEffect(() => {
    if (!orgId) {
      setCustomers([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = subscribeToCustomers(
      orgId,
      data => {
        const sorted = [...data].sort(
          (a, b) =>
            a.firstName.localeCompare(b.firstName) ||
            a.lastName.localeCompare(b.lastName),
        );
        setCustomers(sorted);
        setLoading(false);
      },
      err => {
        setError(err);
        setLoading(false);
      },
    );

    return unsubscribe;
  }, [orgId, tick]);

  return { customers, loading, error, refetch };
}
